import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { Trans } from '@lingui/react';
import { Formik } from 'formik';
import { Modal, Body } from '@organisms/modals/base';
import Styled from 'styled-components';
import rangeSlider from '@components/form/RangeSlider';
import button from '@components/buttons';

const SUBMIT_MOOD_TRACKER = gql`
  mutation addMoodTrackerAnswer(
    $kind: SurveyKind!
    $moodtrackerId: ID!
    $answer1: Int!
    $answer2: Int!
    $answer3: Int!
    $answer4: Int!
  ) {
    addMoodTrackerAnswer(
      kind: $kind
      moodTrackerId: $moodtrackerId
      answer1: $answer1
      answer2: $answer2
      answer3: $answer3
      answer4: $answer4
    ) {
      errors
      status
    }
  }
`;

const StyledModal = Styled(Modal)`
  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    .ModalWrapper {
      width: 830px;
      margin: 0;
    }
  }
  `;

const ModalBody = Styled(Body)`
  padding: 30px 10px;
  
  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    padding: 30px;
  }
`;

const Title = Styled.p`
  font-size: 18px;
  text-align: center;
  font-weight: 600;
  margin: 0 auto;
`;

const Description = Styled.p`
  font-size: 14px;
  text-align: center;
`;

const ContentWrapepr = Styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 60px;
  border-top: 1px solid #e3e3e3;

  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    padding-top: 30px;
  }
`;

const Question = Styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: 3px;
  padding: 18px 5px;
  margin-bottom: 50px;
  background-color: ${({ theme }) => theme.colors.primary['blue-4']};
  
  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    flex-direction: row;
    padding: 20px;
    margin-bottom: 20px;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const MinMaxLabel = Styled.p`
  flex: 1;
  flex-shrink: 0;
  position: absolute;
  top: -25px;
  font-size: 14px;
  font-weight: 500;
  margin: 0;

  :first-of-type {
    left: 20px;
    text-align: left;
  }

  :last-of-type {
    right: 20px;
    text-align: right;
  }
  
  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    position: static;
    margin-top: 0;
  }
`;

const RangeSlider = Styled(rangeSlider)`
  flex-grow: 1;
  max-width: 90%;
  width: 430px;
  margin: 10px auto 15px auto;
  z-index: 1;
  
  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    max-width: 430px;
    margin: 0 auto;
  }
`;

const Error = Styled.p`
  font-size: 16px;
  color: ${props => props.theme.colors.secondary['red-1']};
  text-align: center;
  margin-top: 0;
`;

const Footer = Styled.div`
  padding: 15px;
  background-color: #f7f7f7;
`;

const Button = Styled(button)`
  display: block;
  width: 340px;
  margin: 0 auto;

  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    width: 140px;
  }
`;

const questions = {
  answer1: {
    minLabel: '(-5) Orolig',
    maxLabel: 'Lugn (+5)',
    name: 'answer1',
    initialValue: 0,
  },
  answer2: {
    minLabel: '(-5) Irriterad',
    maxLabel: 'Avslappnad (+5)',
    name: 'answer2',
    initialValue: 0,
  },
  answer3: {
    minLabel: '(-5) Kraftlös',
    maxLabel: 'Energisk (+5)',
    name: 'answer3',
    initialValue: 0,
  },
  answer4: {
    minLabel: '(-5) Slö',
    maxLabel: 'Pigg (+5)',
    name: 'answer4',
    initialValue: 0,
  },
};

const initialValues = Object.keys(questions).reduce((acc, key) => {
  acc[questions[key].name] = questions[key].initialValue;
  return acc;
}, {});

declare type Props = {
  moodTracker: MoodTracker;
  title: string;
  desc: string;
  open?: boolean;
  onComplete(any): void;
};

const MoodTrackerModalHook = ({
  title,
  desc,
  moodTracker,
  open = false,
  onComplete,
}: Props): [JSX.Element, { openModal(): void; closeModal(): void }] => {
  const [isOpen, setIsOpen] = useState(open);
  const [submitMoodTracker, { loading, data, error }] = useMutation(
    SUBMIT_MOOD_TRACKER,
    {
      onCompleted(data) {
        if (data.addMoodTrackerAnswer.status === true) {
          onComplete(data);
        }
      },
    }
  );

  let errorMessage;
  if (error?.graphQLErrors?.[0].message) {
    errorMessage = error?.graphQLErrors?.[0].message;
  } else if (data?.errors) {
    errorMessage = data.errors?.[0];
  }

  const onClose = () => {
    setIsOpen(false);
  };

  const onOpen = () => {
    setIsOpen(true);
  };

  const Content = isOpen ? (
    <StyledModal
      open={isOpen}
      handleClose={onClose}
      data-testid="MoodTrackerModal"
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => {
          const variables = {
            kind: moodTracker.kind,
            moodtrackerId: moodTracker.id,
            ...values,
          };
          submitMoodTracker({
            variables,
          });
        }}
      >
        {({ handleSubmit, setFieldValue }) => {
          return (
            <form onSubmit={handleSubmit}>
              <ModalBody pushFooterDown={110}>
                <Title>{title}</Title>
                <Description>{desc}</Description>
                <ContentWrapepr>
                  {Object.keys(questions).map(key => (
                    <Question key={key}>
                      <MinMaxLabel>{questions[key].minLabel}</MinMaxLabel>
                      <RangeSlider
                        name={questions[key].name}
                        onChange={(_, value) => {
                          setFieldValue(key, value);
                        }}
                      />
                      <MinMaxLabel>{questions[key].maxLabel}</MinMaxLabel>
                    </Question>
                  ))}
                </ContentWrapepr>
              </ModalBody>
              {errorMessage && <Error>{errorMessage}</Error>}
              <Footer>
                <Button
                  loading={loading}
                  data-testid="MoodTrackerModal--Button"
                >
                  <Trans id="phasedProgram.moodtracker_modal.submit_button" />
                </Button>
              </Footer>
            </form>
          );
        }}
      </Formik>
    </StyledModal>
  ) : (
    <React.Fragment />
  );

  return [Content, { openModal: onOpen, closeModal: onClose }];
};

export default MoodTrackerModalHook;
